import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import roleService from '../../services/roleService';
import permissionService from '../../services/permissionService';
import { toast } from 'react-hot-toast';

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [modules, setModules] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [r, p, m] = await Promise.all([
        roleService.listRoles(),
        permissionService.listPermissions({ module: moduleFilter || undefined, limit: 500 }),
        permissionService.listModules(),
      ]);
      setRoles(r.data?.roles || r.data || []);
      setPermissions(p.data?.permissions || p.data || []);
      setModules(m.data?.modules || m.modules || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load roles/permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [moduleFilter]);

  const createRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      await roleService.createRole({ name: newRoleName });
      setNewRoleName('');
      toast.success('Role created');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Create role failed');
    }
  };

  const deleteRole = async (role) => {
    if (!confirm('Delete role?')) return;
    try {
      await roleService.deleteRole(role.id);
      toast.success('Role deleted');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Delete role failed');
    }
  };

  const togglePermission = async (roleId, permissionId, has) => {
    try {
      if (has) {
        await roleService.revokePermissionFromRole(roleId, permissionId);
      } else {
        await roleService.grantPermissionToRole(roleId, permissionId);
      }
      toast.success('Permissions updated');
      load();
    } catch (e) {
      console.error(e);
      toast.error('Permission update failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
            <p className="text-gray-600">Manage system roles and their permissions</p>
          </div>
          <div className="flex items-center space-x-2">
            <input className="border rounded px-3 py-2" placeholder="New role name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
            <button onClick={createRole} className="px-3 py-2 bg-blue-600 text-white rounded flex items-center"><PlusIcon className="w-5 h-5 mr-1"/>Create</button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Filter by module:</label>
                <select className="border rounded px-2 py-1" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
                  <option value="">All</option>
                  {modules.map((mod) => (
                    <option key={mod} value={mod}>{mod}</option>
                  ))}
                </select>
              </div>
              <button className="text-sm px-3 py-1 bg-indigo-600 text-white rounded" onClick={async () => { try { await permissionService.seedTaskPermissions(); toast.success('Task permissions ensured'); load(); } catch(e){ toast.error('Failed to seed'); } }}>Ensure Task Permissions</button>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  {permissions.map((p) => (
                    <th key={p.id} className="px-3 py-3 text-xs font-medium text-gray-500 uppercase text-left">{p.name}</th>
                  ))}
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {roles.map((role) => {
                  const rolePermIds = new Set((role.Permissions || role.permissions || []).map((x) => x.id));
                  return (
                    <tr key={role.id}>
                      <td className="px-6 py-4 font-medium">{role.name}</td>
                      {permissions.map((p) => {
                        const has = rolePermIds.has(p.id);
                        return (
                          <td key={p.id} className="px-3 py-2">
                            <input type="checkbox" checked={has} onChange={() => togglePermission(role.id, p.id, has)} />
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 text-right">
                        <button className="text-red-600 hover:text-red-800" onClick={() => deleteRole(role)}>
                          <TrashIcon className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesPermissions;



